import { Observable, BehaviorSubject, of as observableOf } from 'rxjs';
import { mergeMap, catchError, share } from "rxjs/operators";
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { PayrollDelegate } from "./payroll.delegate";
import
{
    IEmployeeExpense,
    INewEmployeeExpenseDocument,
    INewEmployeePayroll,
    INewEmployeeRefunds,
    IEmployeeExpenseList,
    INewEmployeePaystub,
    INewEmployeeTaxDocument,
    IEmployeePayroll,
    IPayroll,
    ITaxDocument,
    IEmployeeCheck,
    INewEmployeeCheck,
    IUpdateEmployeeCheck
} from "./payroll.interface";
import { AuthenticationService } from "../AuthenticationService";
import { LoggerUtil } from "../LoggerService";
import { IEmployeeInsurance, IEmployeePayrollUpdate } from '../EmployeeService';

export class PayrollService
{
    static logger: LoggerUtil = LoggerUtil.getLoggerUtil("PayrollService");

    private employeeChecks: IEmployeeCheck[] = null;
    private employeeRefunds: INewEmployeeRefunds[] = null;
    private employeeExpenses: IEmployeeExpenseList[] = null;

    private employeeChecksSubject: BehaviorSubject<IEmployeeCheck[]> = new BehaviorSubject(null);
    private employeeChecksFilteredSubject: BehaviorSubject<IEmployeeCheck[]> = new BehaviorSubject(null);

    private employeessRefundsSubject: BehaviorSubject<INewEmployeeRefunds[]> = new BehaviorSubject(null);
    private employeessRefundsFilteredSubject: BehaviorSubject<INewEmployeeRefunds[]> = new BehaviorSubject(null);
    private employeessExpensesSubject: BehaviorSubject<IEmployeeExpenseList[]> = new BehaviorSubject(null);
    private employeessExpensesFilteredSubject: BehaviorSubject<IEmployeeExpenseList[]> = new BehaviorSubject(null);

    public allEmployeeChecks: Observable<IEmployeeCheck[]> = this.employeeChecksSubject;
    public allEmployeeChecksFiltered: Observable<IEmployeeCheck[]> = this.employeeChecksFilteredSubject;

    public allEmployeeRefunds: Observable<INewEmployeeRefunds[]> = this.employeessRefundsSubject;
    public allEmployeeRefundsFiltered: Observable<INewEmployeeRefunds[]> = this.employeessRefundsFilteredSubject;
    public allEmployeeExpenses: Observable<IEmployeeExpenseList[]> = this.employeessExpensesSubject;
    public allEmployeeExpensesFiltered: Observable<IEmployeeExpenseList[]> = this.employeessExpensesFilteredSubject;

    private employeePaystubsData: IPayroll[] = null;
    private employeePaystubsSubject: BehaviorSubject<IPayroll[]> = new BehaviorSubject(null);
    public employeePaystubs: Observable<IPayroll[]> = this.employeePaystubsSubject;

    private taxData: ITaxDocument[] = null;
    private taxSubject: BehaviorSubject<ITaxDocument[]> = new BehaviorSubject(null);
    public taxes: Observable<ITaxDocument[]> = this.taxSubject;

    private employeeInsuranceData: IEmployeeInsurance[] = null;
    private employeeInsuranceSubject: BehaviorSubject<IEmployeeInsurance[]> = new BehaviorSubject(null);
    public employeeInsurance: Observable<IEmployeeInsurance[]> = this.employeeInsuranceSubject;

    private employeeParollUpdateData: IEmployeePayrollUpdate[] = null;
    private employeeParollUpdateSubject: BehaviorSubject<IEmployeePayrollUpdate[]> = new BehaviorSubject(null);
    public employeeParollUpdate: Observable<IEmployeePayrollUpdate[]> = this.employeeParollUpdateSubject;

    public selectedDate = null;

    constructor(private payrollDelegate: PayrollDelegate, private authenticationService: AuthenticationService)
    {
    }

    public setSelectedDate(selectedDate: Date)
    {
        this.selectedDate = selectedDate;
    }


    public getEmployeePayrollUpdates(year: number, updateType: string): Observable<boolean>
    {
        const orgId = this.authenticationService.GetOrgId();

        var response = this.payrollDelegate.getEmployeePayrollUpdate(orgId, year, updateType);

        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(response: IEmployeePayrollUpdate[])
        {
            this.employeeParollUpdateData = response;
            this.employeeParollUpdateSubject.next(this.employeeParollUpdateData);
        }

        function onEmployeeFault(error: any)
        {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }

    public getEmployeeInsurance(year: number, status: boolean): Observable<boolean>
    {
        const orgId = this.authenticationService.GetOrgId();

        var response = this.payrollDelegate.getEmployeeInsurance(orgId, year, status);

        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(insurance: IEmployeeInsurance[])
        {
            this.employeeInsuranceData = insurance.map((data: any) =>
            {
                data.healthInsurancePlanId = data.healthInsuranceDetail ? data.healthInsuranceDetail.insurancePlanId : 0;
                data.dentalInsurancePlanId = data.dentalInsuranceDetail ? data.dentalInsuranceDetail.insurancePlanId : 0;
                data.visionInsurancePlanId = data.visionInsuranceDetail ? data.visionInsuranceDetail.insurancePlanId : 0;
                return data;
            });
            this.employeeInsuranceSubject.next(this.employeeInsuranceData);
        }

        function onEmployeeFault(error: any)
        {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }

    public getEmployeeInsuranceById(employeInsuranceId: string): IEmployeeInsurance
    {
        return this.employeeInsuranceData.find((advanceDataItem: IEmployeeInsurance) =>
            advanceDataItem.employeeInsuanceId === employeInsuranceId);
    }

    public getEmployeepayrollUpdateById(employeePayrollUpdateId: string): IEmployeePayrollUpdate
    {
        return this.employeeParollUpdateData.find((advanceDataItem: IEmployeePayrollUpdate) =>
            advanceDataItem.employeePayrollUpdateId === employeePayrollUpdateId);
    }


    public getTaxes(taxYear: number): Observable<boolean>
    {
        const orgId = this.authenticationService.GetOrgId();
        var response = this.payrollDelegate.getTaxesByYear(taxYear, orgId);

        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(taxes: ITaxDocument[])
        {
            this.taxData = taxes;
            this.taxSubject.next(this.taxData);
        }

        function onEmployeeFault(error: any)
        {
            //PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }


    public getEmployeePayroll(payrollDateId: string): Observable<boolean>
    {
        var response = this.payrollDelegate.getEmployeePaystubsByPayrollDate(payrollDateId);

        response.subscribe(onEmployeeSuccess.bind(this), onEmployeeFault.bind(this));

        return response.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onEmployeeSuccess(employeePaystubs: IPayroll[])
        {
            this.employeePaystubsData = employeePaystubs;
            this.employeePaystubsSubject.next(this.employeePaystubsData);
        }

        function onEmployeeFault(error: any)
        {
            // PayrollService.logger.error(`getEmployeePayroll Fault - Error (${JSON.stringify(error)})`);
        }
    }


    public getAllEmployeeChecks(): void
    {
        if (this.employeeChecks !== null && this.employeeChecks.length > 0)
        {
            return;
        }
        const orgId = this.authenticationService.GetOrgId();
        var results = this.payrollDelegate.getAllEmployeeChecks(orgId);
        results.subscribe(data =>
        {

            const employeesAdvance = data.data.employeeChecks.map(advance =>
            {
                return {
                    employeeCheckId: advance.employeeCheckId,
                    checkId: advance.checkId,
                    employeeId: advance.employeeId,
                    comments: advance.comments,
                    check: advance.check.check,
                    amount: advance.check.amount,
                    deliveryType: advance.check.deliveryType,
                    employeeName: advance.employee.employeeFullName,
                    issuedDate: advance.check.issuedDate,
                    checkCategory: advance.check.checkCategory,
                    employeeCheckDocuments: this.normalizeDocuments(advance.employeeCheckDocuments)

                } as IEmployeeCheck;
            });
            this.employeeChecks = employeesAdvance;
            this.employeeChecksSubject.next(this.employeeChecks);
            this.employeeChecksFilteredSubject.next(this.employeeChecks);
        });
    }

    public getAdvanceById(employeeCheckId: string): IEmployeeCheck
    {
        return this.employeeChecks.find((advanceDataItem: IEmployeeCheck) =>
            advanceDataItem.employeeCheckId === employeeCheckId);
    }

    public getRefundById(refundId: string): INewEmployeeRefunds
    {
        return this.employeeRefunds.find((refundDataItem: INewEmployeeRefunds) =>
            refundDataItem.employeeRefundId === refundId);
    }

    public getExpensesById(expenseId: string): IEmployeeExpenseList
    {
        return this.employeeExpenses.find((expenseDataItem: IEmployeeExpenseList) =>
            expenseDataItem.employeeExpenseId === expenseId);
    }

    public createNewEmployeeCheck(employeeAdvance: INewEmployeeCheck): Observable<boolean>
    {
         var result = this.payrollDelegate.createNewEmployeeAdvance({
            ...employeeAdvance,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(null);
            }
            const employeesAdvance = response.data.employeeChecks.map(advance =>
            {
                return {
                    employeeCheckId: advance.employeeCheckId,
                    checkId: advance.checkId,
                    employeeId: advance.employeeId,
                    comments: advance.comments,
                    check: advance.check.check,
                    amount: advance.check.amount,
                    deliveryType: advance.check.deliveryType,
                    employeeName: advance.employee.employeeFullName,
                    issuedDate: advance.check.issuedDate,
                    checkCategory: advance.check.checkCategory,

                    employeeCheckDocuments: this.normalizeDocuments(advance.employeeCheckDocuments)

                } as IEmployeeCheck;
            });
            this.employeeChecks = employeesAdvance;
            this.employeeChecksSubject.next(this.employeeChecks);
            this.employeeChecksFilteredSubject.next(this.employeeChecks);
            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public createNewEmployeePayrollUpdate(empInsurance: IEmployeePayrollUpdate): Observable<boolean>
    {
        var result = this.payrollDelegate.createNewEmployeePayrollUpdate({
            ...empInsurance,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public updateEmployeePayrollUpdate(empInsurance: IEmployeePayrollUpdate): Observable<boolean>
    {
        var result = this.payrollDelegate.updateEmployeePayrollUpdate({
            ...empInsurance,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public createNewEmployeeInsurance(empInsurance: IEmployeeInsurance): Observable<boolean>
    {
        var result = this.payrollDelegate.createNewEmployeeInsurance({
            ...empInsurance,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public updateNewEmployeeInsurance(empInsurance: IEmployeeInsurance): Observable<boolean>
    {
        var result = this.payrollDelegate.updateEmployeeInsurance({
            ...empInsurance,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }
    public createNewEmployeePayroll(employeePaystub: INewEmployeePaystub): Observable<boolean>
    {
         var result = this.payrollDelegate.createNewEmployeePaystub({
            ...employeePaystub,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public createNewEmployeeTaxDocument(employeeTaxDoc: INewEmployeeTaxDocument): Observable<boolean>
    {
         var result = this.payrollDelegate.createNewEmployeeTaxDocument({
            ...employeeTaxDoc,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }

            return observableOf(true);

        }),
            catchError((error: any) => observableOf(error)),
            share());
    }

    public updateEmployeeAdvance(employeeAdvance: IUpdateEmployeeCheck): Observable<boolean>
    {
         var result = this.payrollDelegate.updateEmployeeAdvance({
            ...employeeAdvance,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }
            const employeesAdvance = response.data.employeeChecks.map(advance =>
            {
                return {
                    employeeCheckId: advance.employeeCheckId,
                    checkId: advance.checkId,
                    employeeId: advance.employeeId,
                    comments: advance.comments,
                    check: advance.check.check,
                    amount: advance.check.amount,
                    deliveryType: advance.check.deliveryType,
                    employeeName: advance.employee.employeeFullName,
                    issuedDate: advance.check.issuedDate,
                    checkCategory: advance.check.checkCategory,

                    employeeCheckDocuments: this.normalizeDocuments(advance.employeeCheckDocuments)

                } as IEmployeeCheck;
            });
            this.employeeChecks = employeesAdvance;
            this.employeeChecksSubject.next(this.employeeChecks);
            this.employeeChecksFilteredSubject.next(this.employeeChecks);
            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public getAllEmployeeRefunds(): void
    {
        if (this.employeeRefunds !== null && this.employeeRefunds.length > 0)
        {
            return;
        }
        const orgId = this.authenticationService.GetOrgId();
        var results = this.payrollDelegate.getAllEmployeeRefunds(orgId);
        results.subscribe(data =>
        {

            const employeesRefunds = data.data.employeeRefunds.map(employee =>
            {
                return {
                    employeeId: employee.employeeId,
                    payrollDateId: employee.payrollDateId,
                    amount: employee.amount,
                    comments: employee.comments,
                    employeeRefundId: employee.employeeRefundId,
                    createdDateTime: employee.createdDateTime,
                    createdBy: employee.createdBy,
                    employeeName: employee.employee.firstName,
                    payrollDate: employee.payrollDate.payDate
                } as INewEmployeeRefunds;
            });
            this.employeeRefunds = employeesRefunds;
            this.employeessRefundsSubject.next(this.employeeRefunds);
            this.employeessRefundsFilteredSubject.next(this.employeeRefunds);
        });
    }

    public createNewEmployeeRefunds(employeeRefund: INewEmployeePayroll): Observable<boolean>
    {
        var result = this.payrollDelegate.createEmployeeRefund({
            ...employeeRefund,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }
            const employeesRefunds = response.data.employeeRefunds.map(employee =>
            {
                return {
                    employeeId: employee.employeeId,
                    payrollDateId: employee.payrollDateId,
                    amount: employee.amount,
                    comments: employee.comments,
                    employeeRefundId: employee.employeeRefundId,
                    createdDateTime: employee.createdDateTime,
                    createdBy: employee.createdBy,
                    employeeName: employee.employee.firstName,
                    payrollDate: employee.payrollDate.payDate
                } as INewEmployeeRefunds;
            });
            this.employeeRefunds = employeesRefunds;
            this.employeessRefundsSubject.next(this.employeeRefunds);
            this.employeessRefundsFilteredSubject.next(this.employeeRefunds);
            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public getAllEmployeeExpense(): void
    {
        if (this.employeeExpenses !== null && this.employeeExpenses.length > 0)
        {
            return;
        }
        const orgId = this.authenticationService.GetOrgId();
        var results = this.payrollDelegate.getAllEmployeeExpenses(orgId);
        results.subscribe(data =>
        {

            const employeesExpenses = data.data.employeeExpenses.map(expense =>
            {
                return {
                    employeeId: expense.employeeId,
                    employeeExpenseId: expense.employeeExpenseId,
                    totalAmount: expense.totalAmount,
                    submisionDate: expense.submisionDate,
                    expenseDetails: expense.employeeExpenseDetail,
                    documents: this.normalizeDocuments(expense.employeeExpenseDocument),
                    employeeName: expense.employee.firstName
                } as IEmployeeExpenseList;
            });
            this.employeeExpenses = employeesExpenses;
            this.employeessExpensesSubject.next(this.employeeExpenses);
            this.employeessExpensesFilteredSubject.next(this.employeeExpenses);
        });
    }

    public createNewEmployeeExpenses(employeeExpenses: IEmployeeExpense): Observable<boolean>
    {
        var result = this.payrollDelegate.createEmployeeExpense({
            ...employeeExpenses,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(false);
            }
            const employeesExpenses = response.data.employeeExpenses.map(expense =>
            {
                return {
                    employeeId: expense.employeeId,
                    employeeExpenseId: expense.employeeExpenseId,
                    totalAmount: expense.totalAmount,
                    submisionDate: expense.submisionDate,
                    expenseDetails: expense.employeeExpenseDetail,
                    documents: this.normalizeDocuments(expense.employeeExpenseDocument),
                    employeeName: expense.employee.firstName
                } as IEmployeeExpenseList;
            });
            this.employeeExpenses = employeesExpenses;
            this.employeessExpensesSubject.next(this.employeeExpenses);
            this.employeessExpensesFilteredSubject.next(this.employeeExpenses);
            return observableOf(true);

        }),
            catchError((error: any) => observableOf(null)),
            share());
    }

    public uploadEmployeeDocuments(documents: INewEmployeeExpenseDocument): Observable<any>
    {
         var result = this.payrollDelegate.uploadExpenseDocument({
            ...documents,
            ...this.authenticationService.getBaseRequest
        });
        return result.pipe(mergeMap((response: any) =>
        {
            if (!response)
            {
                return observableOf(response);
            }

            return observableOf(response.data.employeeAdvanceId);

        }),
            catchError((error: any) => observableOf(error)),
            share());
    }

    public filterAdvances(searchTerm: string)
    {
        let data = this.employeeChecks as any as IEmployeePayroll[];

        //if (searchTerm)
        //{
        //    data = this.employeeChecks.filter(x =>
        //        x.employeeName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
        //        || x.payrollDate.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
        //        || x.amount === Number(searchTerm.trim())
        //    );
        //}

        //this.employeeChecksFilteredSubject.next(data);
    }

    public filterRefunds(searchTerm: string)
    {
        let data = this.employeeRefunds;

        if (searchTerm)
        {
            data = this.employeeRefunds.filter(x =>
                x.employeeName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
                || x.payrollDate.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }

        this.employeessRefundsFilteredSubject.next(data);
    }

    public filterExpenses(searchTerm: string)
    {
        let data = this.employeeExpenses;

        if (searchTerm)
        {
            data = this.employeeExpenses.filter(x =>
                x.employeeName.trim().toLowerCase().includes(searchTerm.trim().toLowerCase())
            );
        }

        this.employeessExpensesFilteredSubject.next(data);
    }
    private normalizeDocuments(documents: any)
    {
        return documents.map(document =>
        {
            document.configurationValue = document.configuration ? document.configuration.configurationValue : "";
            return document;
        });
    }
}